import {ApplicationData} from '@agenium-ai/agenium-client-sdk/dist/models';
import {ParameterStore} from '../../mobx/ParameterStore/ParameterStore';
import {APPROVED_AS_APPLIED, APPROVED_BETTER_THAN_APPLIED, APPROVED_WORSE_THAN_APPLIED} from '../../../Agent/constants';

/**
 * Converts table rating values to numeric values for comparison
 */
const convertTableRatingToNumber = (tableRating: string | null | undefined): number => {
    if (!tableRating) return 0;

    // Handle prosper table rating format
    if (tableRating === 'NotRated') return 0;
    if (tableRating === 'B') return 2;

    // Handle agenium table rating format (e.g., "Table 4")
    const match = tableRating.match(/Table (\d+)/);
    if (match) {
        return parseInt(match[1], 10);
    }

    return 0;
};

/**
 * Maps submitted and approved risk classes to an approval rating.
 * Returns null if the combination is not valid or inputs are missing.
 */
export const mapToApprovalRating = (
    submittedRiskClass: string | null | undefined,
    approvedRiskClass: string | null | undefined,
    submittedTableRating: string | null | undefined = null,
    approvedTableRating: string | null | undefined = null,
): string | null => {
    if (!submittedRiskClass || !approvedRiskClass) {
        return null;
    }

    // Mapping table based on provided data
    const mappingTable: Record<string, Record<string, string>> = {
        SuperPreferredNonTobacco: {
            'Super Preferred': APPROVED_AS_APPLIED,
            'Super Preferred Non Tobacco': APPROVED_AS_APPLIED,
            Preferred: APPROVED_WORSE_THAN_APPLIED,
            'Preferred Non Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Standard Non Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Substandard Non Tobacco': APPROVED_WORSE_THAN_APPLIED,
            Substandard: APPROVED_WORSE_THAN_APPLIED,
            'Preferred Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Standard Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Substandard Tobacco': APPROVED_WORSE_THAN_APPLIED,
        },
        PreferredNonTobacco: {
            'Super Preferred': APPROVED_BETTER_THAN_APPLIED,
            'Super Preferred Non Tobacco': APPROVED_BETTER_THAN_APPLIED,
            Preferred: APPROVED_AS_APPLIED,
            'Preferred Non Tobacco': APPROVED_AS_APPLIED,
            'Standard Non Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Substandard Non Tobacco': APPROVED_WORSE_THAN_APPLIED,
            Substandard: APPROVED_WORSE_THAN_APPLIED,
            'Preferred Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Standard Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Substandard Tobacco': APPROVED_WORSE_THAN_APPLIED,
        },
        StandardNonTobacco: {
            'Super Preferred': APPROVED_BETTER_THAN_APPLIED,
            'Super Preferred Non Tobacco': APPROVED_BETTER_THAN_APPLIED,
            Preferred: APPROVED_BETTER_THAN_APPLIED,
            'Preferred Non Tobacco': APPROVED_BETTER_THAN_APPLIED,
            'Standard Non Tobacco': APPROVED_AS_APPLIED,
            'Substandard Non Tobacco': APPROVED_WORSE_THAN_APPLIED,
            Substandard: APPROVED_WORSE_THAN_APPLIED,
            'Preferred Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Standard Tobacco': APPROVED_WORSE_THAN_APPLIED,
        },
        PreferredTobacco: {
            'Preferred Tobacco': APPROVED_AS_APPLIED,
            'Standard Tobacco': APPROVED_WORSE_THAN_APPLIED,
            'Substandard Tobacco': APPROVED_WORSE_THAN_APPLIED,
            Substandard: APPROVED_WORSE_THAN_APPLIED,
        },
        StandardTobacco: {
            'Preferred Tobacco': APPROVED_BETTER_THAN_APPLIED,
            'Standard Tobacco': APPROVED_AS_APPLIED,
            'Substandard Tobacco': APPROVED_WORSE_THAN_APPLIED,
            Substandard: APPROVED_WORSE_THAN_APPLIED,
        },
    };

    const baseRating = mappingTable[submittedRiskClass]?.[approvedRiskClass] || null;

    // If we have table ratings and base rating would be "Approved as Applied",
    // refine based on table rating comparison
    if (baseRating === APPROVED_AS_APPLIED && (submittedTableRating || approvedTableRating)) {
        const submittedTableNumber = convertTableRatingToNumber(submittedTableRating);
        const approvedTableNumber = convertTableRatingToNumber(approvedTableRating);

        if (approvedTableNumber > submittedTableNumber) {
            return APPROVED_WORSE_THAN_APPLIED;
        } else if (approvedTableNumber < submittedTableNumber) {
            return APPROVED_BETTER_THAN_APPLIED;
        }
    }

    return baseRating;
};

/**
 * Gets the approval rating based on application data and parameter store.
 */
export const getApprovalRating = (
    applicationData: ApplicationData[] | undefined,
    parameterStore: ParameterStore,
): string | null => {
    const submittedRiskClass = parameterStore.quote.prosperRiskClass.value;
    const approvedRiskClass = applicationData?.find((item) => item.key === 'Case_UWDecision_Overall')?.value;
    const submittedTableRating = parameterStore.quote.prosperTableRating.value;
    const approvedTableRating = applicationData?.find(
        (item) => item.key === 'Case_UWDecision_HxOutcome_TableRating',
    )?.value;

    return mapToApprovalRating(submittedRiskClass, approvedRiskClass, submittedTableRating, approvedTableRating);
};
